import { Avatar, Box, SxProps, Theme } from '@mui/material'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress/C1CircularProgress'
import { C1Icon } from 'components/core/icons/C1Icon'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { CircleAlert, Contact, Shapes } from 'lucide-react'
import Image from 'next/image'
import { ProfileTypeIconRef } from 'pbts/c1/api/assets/v1/profile_type_icons'
import { getProfileTypeIconApi } from 'redux_toolkit/profile-type'
import { AvatarSize, getAvatarIconSize } from './AvatarSizes'
import { avatarStyles, avatarIconStyles } from './styles'

export type ProfileTypeAvatarProps = {
    profileTypeName: string
    profileTypeId: string
    size?: AvatarSize
    sx?: SxProps
}

const ProfileTypeIconBox = (
    { profileTypeId, profileTypeName, iconSx, pixelSize }: {
        profileTypeId: string, profileTypeName: string, iconSx: SxProps<Theme>, pixelSize: number
    },
): React.ReactElement => {
    const { data, isLoading, isError, error } = getProfileTypeIconApi.useMultiGet(ProfileTypeIconRef.create({ profileTypeId }))
    if (data && data.srcSet?.candidates?.length) {
        return <Image
            src={data.src}
            loader={({ width, src }): string => {
                if (!data.srcSet || !data.srcSet.candidates || data.srcSet.candidates.length === 0) {
                    return src
                }
                const keys = Object.keys(data.srcSet?.candidates || {}).map(Number).sort((a, b) => b - a)

                for (const key of keys) {
                    if (key >= width) {
                        return data.srcSet?.candidates[key].src || ''
                    }
                }

                // Check if keys array has any elements before accessing the last one
                if (keys.length > 0) {
                    return data.srcSet?.candidates[keys[keys.length - 1]].src || ''
                }
                return src
            }}
            alt={profileTypeName || ''}
            title={profileTypeName}
            width={pixelSize}
            height={pixelSize}
        />
    }

    if (isLoading) {
        return <Box sx={iconSx}>
            <C1CircularProgress />
        </Box>
    }
    if (isError) {
        // Treat NOT_FOUND as an expected "no icon" case and use the fallback
        if (error?.code === 'NOT_FOUND') {
            return <Box sx={iconSx}>
                <C1Icon icon={Shapes} sx={{ height: '100%', width: '100%', color: 'grey.800' }} />
            </Box>
        }
        return <Box sx={iconSx}>
            <C1Icon icon={CircleAlert} sx={{ color: 'error.main' }} />
        </Box>
    }
    return <Box sx={iconSx}>
        <C1Icon
            icon={Shapes}
            sx={{ height: '100%', width: '100%', color: 'text.primary' }}
        />
    </Box>
}

export const ProfileTypeAvatar = ({ profileTypeName, profileTypeId, size = 'md', sx }: ProfileTypeAvatarProps): React.ReactElement => {
    const avatarSx = sxc(avatarStyles.avatar, avatarStyles.appAvatar, size && avatarStyles[size], sx)
    const iconSx = avatarIconStyles[`${size}Icon`] || avatarIconStyles.icon
    const pixelSize = getAvatarIconSize(size)

    if (profileTypeId === '') {
        return <Avatar alt={profileTypeName} sx={avatarSx}>
            <C1Icon
                icon={Contact}
                sx={{ height: pixelSize, width: pixelSize, color: 'text.secondary' }}
            />
        </Avatar>
    }

    return <Avatar alt={profileTypeName} sx={avatarSx}>
        <ProfileTypeIconBox profileTypeId={profileTypeId} profileTypeName={profileTypeName} iconSx={iconSx} pixelSize={pixelSize} />
    </Avatar>
}
